import React, { useEffect, useReducer, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import { changeImperfectLegacyPlans, getImperfectLegacyPlans } from 'api/jalapeno';
import { ACCOUNT_QUERY_KEY } from 'api/queries/useAccountQuery';
import { SUBSCRIPTION_QUERY_KEY } from 'api/queries/useSubscriptionQuery';
import PlanCard from 'features/account/my-plan/PlanCard';
import TaxesApply from 'features/account/my-plan/TaxesApply';
import Button from 'honeydew/Button';
import { Divider } from 'honeydew/Divider';
import { Radio, RadioGroup } from 'honeydew/Radio';
import Select from 'honeydew/Select';
import useGlobalStore from 'hooks/useGlobalStore';
import useTailwindTheme from 'hooks/useTailwindTheme';
import logError from 'utils/errorUtils';
import { isIFPlanPreferencesValid } from 'utils/plansUtils';

import ifPlanReducer from './reducer';

function EditIFPlansModule({ account, setError, setIsEditing }) {
    const queryClient = useQueryClient();
    const tailwindTheme = useTailwindTheme();
    const { isMisfitsTheme } = useGlobalStore();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const { cartMinimum, planPreferences = {}, subID } = account ?? {};
    const { id, name, size } = planPreferences.basePlan ?? {};
    const planName = name?.split(',')[0] || '';
    const produceType = name?.split(',')[1] || 'Organic and Conventional';

    const [availablePlans, setAvailablePlans] = useState({ basePlan: [], addOnPlans: [] });
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [isProduceSubmitDisabled, setIsProduceSubmitDisabled] = useState(false);
    const [assortmentValue, setAssortmentValue] = useState(planName);
    const [isEditingPreferences, setIsEditingPreferences] = useState(false);
    const [availablePackSizes, setAvailablePackSizes] = useState([]);
    const [availablePlanTypes, setAvailablePlanTypes] = useState([]);
    const [ifAssortment, setIfAssortment] = useState([]);
    const [showOrderMinError, setShowOrderMinError] = useState(false);
    const [validPreferences, setValidPreferences] = useState(false);

    const [state, dispatch] = useReducer(ifPlanReducer, planPreferences);

    const handleSubmitPlanChange = async (successCallback) => {
        setError('');
        setShowOrderMinError(false);

        const sendParams = {
            basePlanID: state.basePlan.id,
            basePlanSize: state.basePlan.size,
            addOnPlanIDs: state.addOnPlans.map((p) => p.id),
        };

        if (validPreferences) {
            try {
                const response = await changeImperfectLegacyPlans(subID, sendParams);

                if (response.msg === 'Success') {
                    queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEY] });
                    queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });
                    successCallback(false);
                }
            } catch (err) {
                setIsSubmitDisabled(false);
                setIsProduceSubmitDisabled(false);
                setError('Unable to update plan');
            }
        } else {
            setShowOrderMinError(true);
            setIsSubmitDisabled(false);
        }
    };

    const handleCancelEditPreferences = () => {
        setIsEditingPreferences(false);

        return dispatch({
            type: 'setBasePlanData',
            name,
            id,
            size,
        });
    };

    const produceChangeCallback = () => {
        setIsEditingPreferences(false);
        setIsProduceSubmitDisabled(false);
    };

    const handleSubmitProduceChange = () => {
        setIsProduceSubmitDisabled(true);

        if (Number(state.basePlan.id) === Number(id) && state.basePlan.size === size) {
            setIsEditingPreferences(false);
            setIsProduceSubmitDisabled(false);
            return;
        }

        handleSubmitPlanChange(produceChangeCallback);
    };

    const addOnsChangeCallback = () => {
        setIsEditing(false);
        setIsSubmitDisabled(false);
        window.scrollTo({ top: 500, behavior: 'smooth' });
    };

    const handleSubmitAddOnsChange = () => {
        setIsSubmitDisabled(true);

        const newAddOns = state.addOnPlans.map((p) => p.id);
        const previousAddOns = planPreferences.addOnPlans.map((p) => p.id);

        if (newAddOns.sort().join(',') === previousAddOns.sort().join(',')) {
            setIsEditing(false);
            return;
        }

        handleSubmitPlanChange(addOnsChangeCallback);
    };

    const handleSetAssortmentValue = (ifAssortmentPlans, selectedValue) => {
        setAssortmentValue(selectedValue);

        const selectedOption = ifAssortmentPlans?.find((option) => option.value === selectedValue) || {};
        const packSizeOptions = selectedOption.availablePlanTypes[0].availablePackSizes;

        setAvailablePlanTypes(selectedOption.availablePlanTypes);
        setAvailablePackSizes(packSizeOptions);

        const currentPlanID = selectedOption.availablePlanTypes?.find((p) => p.value === state.basePlan.id);
        const newPlanID = currentPlanID ? currentPlanID.value : selectedOption.availablePlanTypes[0].value;

        const currentPackSize = packSizeOptions.find((p) => p.value === state.basePlan.size);
        const newPackSize = currentPackSize ? currentPackSize.value : packSizeOptions[0].value;

        return dispatch({
            type: 'setBasePlanData',
            name: selectedValue,
            id: newPlanID,
            size: newPackSize,
        });
    };

    const handleSetUpdateAssortmentValue = (selectedValue) => {
        handleSetAssortmentValue(ifAssortment, selectedValue);
    };

    const handleSetPlanID = (e) => {
        e.preventDefault();

        const selectedOption = ifAssortment.find((option) => option.value === assortmentValue) || {};
        const currentPlanID = selectedOption.availablePlanTypes?.find((p) => p.value === e.target.value);
        setAvailablePackSizes(currentPlanID?.availablePackSizes);

        return dispatch({
            type: 'setBasePlanID',
            planID: e.target.value,
        });
    };

    const handleSetPlanSize = (e) => {
        e.preventDefault();

        return dispatch({
            type: 'setBasePlanSize',
            size: e.target.value,
        });
    };

    // this function is responsible for shaping the response data from the IF
    // plans endpoint into a usable structure to populate dropdown info
    const generatePlansArray = (plans) => {
        const availablePlansList = [];

        plans?.forEach((plan, i) => {
            const splitName = plan?.name?.split(',')[0] || '';
            const availablePlanNames = availablePlansList.map((p) => p.value);
            const planSizes = [];

            Object.keys(plan.planSizes).forEach((planSize) => {
                const planItems = plan.planSizes[planSize].numItems;
                const planSizeName = planSize === 'XL' ? 'Extra Large' : planSize;
                planSizes.push({
                    name: `${planSizeName} (${planItems} items)`,
                    itemCopy: `${planSizeName} (${planItems} items)`,
                    value: planSize,
                });
            });

            if (!availablePlanNames.includes(splitName)) {
                availablePlansList.push({
                    id: i + 1,
                    value: splitName,
                    itemCopy: splitName,
                    availablePlanTypes: [
                        {
                            id: 1,
                            name: 'Conventional and Organic',
                            itemCopy: 'Conventional and Organic',
                            value: plan.id.toString(),
                            availablePackSizes: planSizes,
                        },
                    ],
                });
            } else {
                availablePlansList.forEach((item) => {
                    if (item.value === splitName) {
                        item.availablePlanTypes.push({
                            id: 2,
                            name: 'Organic Only',
                            itemCopy: 'Organic Only',
                            value: plan.id.toString(),
                            availablePackSizes: planSizes,
                        });
                    }
                });
            }
        });
        setIfAssortment(availablePlansList);
        return availablePlansList;
    };

    useEffect(() => {
        if (availablePlans?.basePlans?.length) {
            const isValid = isIFPlanPreferencesValid(availablePlans, state.addOnPlans, state.basePlan, cartMinimum);
            setValidPreferences(isValid);
            setShowOrderMinError(!isValid);
            setIsSubmitDisabled(!isValid);
        }
    }, [state]);

    useEffect(() => {
        const getAvailablePlanData = async () => {
            try {
                const response = await getImperfectLegacyPlans();
                if (response.data) {
                    setAvailablePlans(response.data.plans);
                    const assortmentPlans = generatePlansArray(response.data.plans.basePlans);
                    handleSetAssortmentValue(assortmentPlans, planName);
                }
            } catch (e) {
                logError(e);
                console.error(e);
            }
        };
        getAvailablePlanData();
    }, []);

    return (
        <div>
            <h4 className="text-body-md font-grotesk-bold">Plan Settings</h4>

            <div>
                <div>
                    <p className="text-body-sm mb-25 mt-5">
                        Select the packs and settings of groceries you want us to pre-fill your cart with each week.
                    </p>
                </div>

                <TaxesApply />

                {!mobile && <Divider />}

                <div className="relative -mx-20 my-20 bg-strawberry bg-opacity-40 p-20 md:m-0 md:bg-white md:p-0">
                    <h4 className="text-body-md mb-15 font-grotesk-bold">Produce Preferences</h4>

                    <div className="text-body-sm mb-25 md:mx-0 md:rounded-lg md:bg-off-white md:bg-opacity-100 md:p-20">
                        <div className="grid grid-cols-[100px_1fr] gap-20">
                            <div className="h-[100px] w-[100px] overflow-hidden rounded-md bg-grey-light">
                                <img
                                    src="https://img-cdn.misfitsmarket.com/boysenberry/images/IF_plans-produce.png"
                                    alt="produce"
                                />
                            </div>
                            <div>
                                <p className="mb-5 font-grotesk-bold">Pick Your Produce</p>
                                <p>
                                    We like to think of produce as the foundation of our boxes. Tell us more about what
                                    you&apos;d like with each order below!
                                </p>
                                {!isEditingPreferences && (
                                    <div className="md:relative">
                                        <button
                                            type="button"
                                            className="link text-body-xs absolute right-20 top-20 md:right-10 md:top-10"
                                            onClick={() => setIsEditingPreferences(true)}
                                        >
                                            Edit
                                        </button>
                                        <ul className="list-disc pl-20 pt-10">
                                            {planName !== '' && <li>{planName}</li>}
                                            <li>{produceType}</li>
                                            <li>{size} Size</li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>

                        {isEditingPreferences && (
                            <>
                                <Divider color="oat" />

                                <div>
                                    <p className="font-grotesk-bold">Options</p>

                                    <RadioGroup
                                        onValueChange={handleSetUpdateAssortmentValue}
                                        defaultValue={assortmentValue}
                                    >
                                        {ifAssortment?.map((option) => (
                                            <div key={option.id} className="my-10">
                                                <Radio
                                                    value={option.value}
                                                    name={option.value}
                                                    labelCopy={<p>{option.value}</p>}
                                                    id={option.value}
                                                    checked={option.value === assortmentValue}
                                                    required
                                                />
                                            </div>
                                        ))}
                                    </RadioGroup>
                                    <div className="md:max-w-[350px]">
                                        <div className="my-20">
                                            <Select
                                                labelId="plan-type-select-label"
                                                id="plan-type-select"
                                                label="Type"
                                                value={state.basePlan.id}
                                                menuItems={availablePlanTypes}
                                                onChange={handleSetPlanID}
                                                isMisfits={isMisfitsTheme}
                                                required
                                            />
                                        </div>
                                        <Select
                                            labelId="pack-size-select-label"
                                            id="pack-size-select"
                                            label="Pack size"
                                            value={state.basePlan.size}
                                            menuItems={availablePackSizes}
                                            onChange={handleSetPlanSize}
                                            isMisfits={isMisfitsTheme}
                                            required
                                        />
                                        <Divider />

                                        <Button
                                            size="md"
                                            type="button"
                                            onClick={handleSubmitProduceChange}
                                            disabled={isProduceSubmitDisabled}
                                        >
                                            Update
                                        </Button>
                                        <button
                                            type="button"
                                            onClick={handleCancelEditPreferences}
                                            className="text-body-sm link mx-auto mt-15 block"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <h4 className="text-body-md mb-15 font-grotesk-bold">Grocery Add-Ons</h4>

                <fieldset>
                    {availablePlans.addOnPlans.map((availablePlan) => {
                        const savedPlan = state?.addOnPlans?.find((plan) => plan.id === availablePlan.id);
                        return (
                            <React.Fragment key={availablePlan.name}>
                                <PlanCard
                                    dispatch={dispatch}
                                    imperfectLegacy
                                    plan={availablePlan}
                                    savedPlan={savedPlan}
                                />
                            </React.Fragment>
                        );
                    })}
                </fieldset>

                <Divider />

                <div>
                    <p className="text-body-xs mb-15">Any updates will go into effect on your next shopping window.</p>

                    {showOrderMinError && (
                        <p className="mb-15 font-grotesk-bold text-error">
                            Pack selections must meet or surpass order minimum. Make additional selection(s).
                        </p>
                    )}

                    <div className="max-w-[350px]">
                        <Button size="md" type="button" onClick={handleSubmitAddOnsChange} disabled={isSubmitDisabled}>
                            Update
                        </Button>
                        <button
                            type="button"
                            onClick={() => setIsEditing(false)}
                            className="text-body-sm link mx-auto mt-15 block"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

EditIFPlansModule.propTypes = {
    account: PropTypes.shape({}),
    setError: PropTypes.func.isRequired,
    setIsEditing: PropTypes.func.isRequired,
};

EditIFPlansModule.defaultProps = {
    account: {},
};

export default EditIFPlansModule;
